import React, { useMemo } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  CalendarCheck2,
  CalendarX2,
  Clock4,
  Landmark,
  MinusCircle,
  PlusCircle,
  WalletCards,
  X,
} from "lucide-react";
import {
  formatCurrency,
  formatDateTime,
  formatMonthLabel,
  formatOvertimeHours,
  formatWorkDays,
} from "./payslipFormat";

/** Các item_code đã được thể hiện bằng dòng cố định bên dưới. */
const HANDLED_ITEM_CODES = new Set([
  "base_salary",
  "allowance",
  "actual_work_days",
  "overtime_amount",
  "unpaid_leave_deduction",
  "adjustment_plus",
  "adjustment_minus",
  "manual_deduction",
  "gross_pay",
  "net_pay",
]);

const MetricChip = ({ icon: Icon, label, value, iconClass }) => (
  <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/70 px-3 py-2.5">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500">
      <Icon size={16} className={iconClass} strokeWidth={2.25} />
    </span>
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="text-sm font-bold text-slate-800 tabular-nums">{value}</p>
    </div>
  </div>
);

const AmountRow = ({ label, amount }) => {
  const isZero = Number(amount || 0) === 0;
  return (
    <div className="flex items-baseline justify-between gap-4 px-4 py-2.5">
      <span className="text-sm text-slate-600">{label}</span>
      <span
        className={`shrink-0 text-sm font-bold tabular-nums ${
          isZero ? "text-slate-300" : "text-slate-800"
        }`}
      >
        {formatCurrency(amount)}
      </span>
    </div>
  );
};

const BreakdownCard = ({ title, icon: Icon, tone, rows, total, totalLabel }) => (
  <div className="overflow-hidden rounded-2xl border border-slate-100">
    <div className={`flex items-center gap-2 px-4 py-3 ${tone.header}`}>
      <Icon size={15} strokeWidth={2.5} />
      <h4 className="text-[11px] font-black uppercase tracking-widest">{title}</h4>
    </div>
    <div className="divide-y divide-slate-50 bg-white">
      {rows.map((row) => (
        <AmountRow key={row.key} label={row.label} amount={row.amount} />
      ))}
    </div>
    <div
      className={`flex items-baseline justify-between gap-4 border-t px-4 py-3 ${tone.footer}`}
    >
      <span className="text-[11px] font-black uppercase tracking-widest">{totalLabel}</span>
      <span className="shrink-0 text-base font-black tabular-nums">{formatCurrency(total)}</span>
    </div>
  </div>
);

const TONES = {
  earning: {
    header: "bg-emerald-50 text-emerald-700",
    footer: "border-emerald-100 bg-emerald-50/60 text-emerald-800",
  },
  deduction: {
    header: "bg-rose-50 text-rose-700",
    footer: "border-rose-100 bg-rose-50/60 text-rose-800",
  },
};

const MyPayslipDetailModal = ({ isOpen, onClose, payslip }) => {
  const breakdown = useMemo(() => {
    if (!payslip) return null;

    const num = (v) => Number(v || 0);
    const extras = (payslip.items || []).filter(
      (item) => item.item_type !== "info" && !HANDLED_ITEM_CODES.has(item.item_code)
    );

    const earningRows = [
      { key: "base_salary", label: "Lương cơ sở", amount: num(payslip.base_salary) },
      { key: "allowance", label: "Phụ cấp", amount: num(payslip.allowance) },
      {
        key: "overtime",
        label: `Tăng ca (${formatOvertimeHours(payslip.overtime_minutes)})`,
        amount: num(payslip.overtime_amount),
      },
      { key: "adjustment_plus", label: "Điều chỉnh cộng", amount: num(payslip.adjustment_plus) },
      ...extras
        .filter((item) => item.item_type === "earning")
        .map((item) => ({ key: item.id, label: item.label, amount: Math.abs(num(item.amount)) })),
    ];

    const deductionRows = [
      {
        key: "unpaid_leave",
        label: `Nghỉ không lương (${formatWorkDays(payslip.unpaid_leave_days)} ngày)`,
        amount: num(payslip.unpaid_leave_deduction),
      },
      { key: "adjustment_minus", label: "Điều chỉnh trừ", amount: num(payslip.adjustment_minus) },
      {
        key: "manual_deduction",
        label: "Thuế / BHXH và khấu trừ khác",
        amount: num(payslip.manual_deduction),
      },
      ...extras
        .filter((item) => item.item_type === "deduction")
        .map((item) => ({ key: item.id, label: item.label, amount: Math.abs(num(item.amount)) })),
    ];

    const earningTotal = earningRows.reduce((sum, row) => sum + row.amount, 0);
    const deductionTotal = deductionRows.reduce((sum, row) => sum + row.amount, 0);

    return { earningRows, deductionRows, earningTotal, deductionTotal };
  }, [payslip]);

  return (
    <AnimatePresence>
      {isOpen && payslip && breakdown && (
        <>
          <motion.div
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-md z-[55]"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 flex items-center justify-center z-[56] p-4"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden border border-slate-100 flex flex-col">
              <div className="px-5 py-4 md:px-6 md:py-5 border-b border-slate-100 flex justify-between items-start gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="p-2 bg-red-600 rounded-xl text-white shrink-0">
                    <WalletCards size={18} />
                  </span>
                  <div className="min-w-0">
                    <h3 className="text-base md:text-lg font-black text-slate-800">
                      Phiếu lương {formatMonthLabel(payslip.year_month)}
                    </h3>
                    <p className="truncate text-xs md:text-sm text-slate-500">
                      {payslip.run_code || "—"} • Công bố {formatDateTime(payslip.published_at)}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="text-slate-400 hover:text-red-600 shrink-0"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-5 py-5 md:px-6 overflow-y-auto flex-1 space-y-5">
                <div className="rounded-2xl border border-red-100 bg-red-50/60 p-4">
                  <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-bold text-red-700/70 uppercase tracking-widest">
                        Thực lĩnh
                      </p>
                      <p className="mt-1 text-2xl md:text-3xl font-black text-red-700 tabular-nums">
                        {formatCurrency(payslip.net_pay)}
                      </p>
                    </div>
                    <div className="flex gap-6">
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Tổng thu nhập
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-emerald-700 tabular-nums">
                          {formatCurrency(breakdown.earningTotal)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          Tổng khấu trừ
                        </p>
                        <p className="mt-0.5 text-sm font-bold text-rose-700 tabular-nums">
                          − {formatCurrency(breakdown.deductionTotal)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <MetricChip
                    icon={CalendarCheck2}
                    iconClass="text-sky-600"
                    label="Ngày công"
                    value={`${formatWorkDays(payslip.actual_work_days)} / ${formatWorkDays(
                      payslip.standard_work_days
                    )} ngày`}
                  />
                  <MetricChip
                    icon={Clock4}
                    iconClass="text-amber-600"
                    label="Tăng ca"
                    value={formatOvertimeHours(payslip.overtime_minutes)}
                  />
                  <MetricChip
                    icon={CalendarX2}
                    iconClass="text-rose-600"
                    label="Nghỉ không lương"
                    value={`${formatWorkDays(payslip.unpaid_leave_days)} ngày`}
                  />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <BreakdownCard
                    title="Thu nhập"
                    icon={PlusCircle}
                    tone={TONES.earning}
                    rows={breakdown.earningRows}
                    total={breakdown.earningTotal}
                    totalLabel="Tổng thu nhập"
                  />
                  <BreakdownCard
                    title="Khấu trừ"
                    icon={MinusCircle}
                    tone={TONES.deduction}
                    rows={breakdown.deductionRows}
                    total={breakdown.deductionTotal}
                    totalLabel="Tổng khấu trừ"
                  />
                </div>

                <div className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50 p-4">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-slate-500">
                    <Landmark size={18} />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                      Tài khoản nhận lương
                    </p>
                    <p className="text-sm font-bold text-slate-800">
                      {payslip.payroll_bank_name_snapshot || "—"}
                      <span className="ml-2 font-semibold text-slate-500 tabular-nums">
                        {payslip.payroll_bank_number_snapshot || "—"}
                      </span>
                    </p>
                  </div>
                </div>

                {payslip.note && (
                  <div className="rounded-2xl bg-amber-50 border border-amber-100 p-4">
                    <p className="text-[10px] font-bold uppercase tracking-wider text-amber-700/80">
                      Ghi chú kế toán
                    </p>
                    <p className="mt-1 text-sm text-amber-900">{payslip.note}</p>
                  </div>
                )}
              </div>

              <div className="px-5 py-4 md:px-6 border-t border-slate-100 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-6 py-2.5 rounded-xl bg-slate-900 text-white font-bold text-sm"
                >
                  Đóng
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default MyPayslipDetailModal;
